// THE EMAIL SIGNATURE, v3 (Karan's brief, 31/10/2026 — Drive: 4.5.1 Track ›
// track-signature-v3/PROMPT.md). ONE implementation for the server push and the
// Track preview: functions/signature/render.js loads the copy that
// scripts/sync-calc-shared.mjs makes in functions/signature-shared/, and
// test/signature-v3.test.mjs fails if that copy drifts from this file.
//
// PURE. No DOM, no Node built-ins, no Firebase: it runs in the browser (the
// Settings › Email signature preview, the profile card) and in the Cloud
// Function exactly the same way, so what the preview shows is what Gmail gets.
//
// TEMPLATE_SOURCE below is Karan's template.source.html VERBATIM (held byte for
// byte against test/fixtures/signature-v3.template.source.html). The renderer
// never edits its static parts; it only:
//   1. drops the leading HTML comment (notes for us, not for a recipient's inbox);
//   2. resolves {{#if x}}…{{/if}} blocks — an empty x removes the block whole,
//      so there is never a blank row or a stray <br>;
//   3. substitutes {{value}} in ONE pass, every value HTML-escaped — data can
//      never name a placeholder and have it filled in;
//   4. for the push, removes the line breaks and indentation BETWEEN lines (every
//      line of the template starts and ends on a tag or a block marker, so no
//      text whitespace is touched) — the pretty source is kept for the preview.
//
// Rules from the brief, §3, and where each one lives:
//   • escape everything ................................ esc() on every value
//   • NAME and TITLE uppercased here, en-GB, never CSS .. toLocaleUpperCase('en-GB')
//   • showLabels only when BOTH numbers render ........... model()
//   • office number 2px/#5b616d under a mobile, else 12px/#14161b
//   • &nbsp; inside the postcode ......................... nbspPostcode()
//   • tel: links from an E.164 derivation; if it fails the href is omitted and
//     a warning is returned for the caller to log ........ toE164()
//   • no <style>, class=, max-width, text-transform, flex, grid — the template
//     has none and nothing here adds any (FORBIDDEN, tested).
//
// The blocked-image stand-ins (§4a) are a PREVIEW AID ONLY: previewDocument()
// swaps the <img> for what Outlook / Gmail / Apple Mail draw instead, inside the
// sandboxed preview frame. The pushed HTML is always the real template.

export const TEMPLATE_SOURCE = `<!--
  ASP email signature — template v3 (31 Oct 2026)
  Renderer: renderSignature(org, user) → string. Rules in PROMPT.md §3.
  Every {{value}} is HTML-escaped by the renderer. Blocks marked {{#if x}}…{{/if}} are omitted entirely when x is empty.
  Colours are ASP design-system tokens: ink #14161b · ink-2 #5b616d · steel #9ba1ad · line #d7dae0 · link #123ad0 · brand-deep #10328b
-->
<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;color:#14161b">
  <tr>
    <td style="padding:0">
      <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse">
        <tr>
          <td valign="top" width="{{logoWidth}}" style="padding:2px 20px 0 0">
            <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse">
              <tr><td style="padding:0"><img src="{{logoUrl}}" alt="{{logoAlt}}" title="{{logoAlt}}" width="{{logoWidth}}" height="{{logoHeight}}" border="0" style="display:block;width:{{logoWidth}}px;height:{{logoHeight}}px;border:0;font-family:Arial,Helvetica,sans-serif;font-size:22px;font-weight:bold;letter-spacing:2px;line-height:{{logoHeight}}px;color:#10328b"></td></tr>
              {{#if descriptorLine1}}<tr><td style="padding:10px 0 0 0;font-family:Arial,Helvetica,sans-serif;font-size:8px;line-height:12px;font-weight:bold;letter-spacing:1px;white-space:nowrap;color:#5b616d">{{descriptorLine1}}{{#if descriptorLine2}}<br>{{descriptorLine2}}{{/if}}</td></tr>{{/if}}
            </table>
          </td>
          <td valign="top" style="padding:0 0 0 20px;border-left:1px solid #d7dae0">
            <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse">
              <tr><td style="padding:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:18px;font-weight:bold;letter-spacing:1px;color:#14161b">{{#if preNominals}}<span style="font-size:13px;font-weight:normal;letter-spacing:0;color:#5b616d">{{preNominals}}</span> {{/if}}{{DISPLAY_NAME}}{{#if postNominals}} <span style="font-size:13px;font-weight:normal;letter-spacing:0;color:#5b616d">{{postNominals}}</span>{{/if}}</td></tr>
              <tr><td style="padding:3px 0 0 0;font-family:Arial,Helvetica,sans-serif;font-size:11px;line-height:14px;letter-spacing:1.5px;color:#5b616d">{{JOB_TITLE}}</td></tr>
              {{#if mobile}}<tr><td style="padding:12px 0 0 0;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:16px;color:#14161b">{{#if showLabels}}<span style="display:inline-block;width:16px;font-size:9px;letter-spacing:1px;color:#9ba1ad">M</span>{{/if}}<a href="tel:{{mobileE164}}" style="color:#14161b;text-decoration:none">{{mobile}}</a></td></tr>{{/if}}
              {{#if officeTel}}<tr><td style="padding:{{officeTelPadding}}px 0 0 0;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:16px;color:#14161b">{{#if showLabels}}<span style="display:inline-block;width:16px;font-size:9px;letter-spacing:1px;color:#9ba1ad">T</span>{{/if}}<a href="tel:{{officeTelE164}}" style="color:{{officeTelColor}};text-decoration:none">{{officeTel}}</a></td></tr>{{/if}}
              <tr><td style="padding:6px 0 0 0;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:16px"><a href="{{websiteUrl}}" style="color:#123ad0;text-decoration:none">{{websiteLabel}}</a></td></tr>
            </table>
          </td>
        </tr>
      </table>
      <table cellpadding="0" cellspacing="0" border="0" role="presentation" width="420" style="border-collapse:collapse;width:420px">
        <tr><td style="padding:16px 0 0 0;font-family:Arial,Helvetica,sans-serif;font-size:10px;line-height:15px;color:#5b616d">{{registeredName}}, registered in {{placeOfRegistration}}, company no. {{companyNumber}}.<br>Registered office: {{registeredOfficeNbsp}}.<br><a href="{{legalUrl}}" style="color:#5b616d;text-decoration:underline">{{legalLabel}}</a></td></tr>
      </table>
    </td>
  </tr>
</table>
`;

/** ASP design-system tokens the template uses (brief §1). */
export const TOKENS = {
  ink: '#14161b', ink2: '#5b616d', steel: '#9ba1ad', line: '#d7dae0', link: '#123ad0', brandDeep: '#10328b',
};

/** Gmail rejects an oversized signature; v3 is ~3.5KB, so 10KB is a tripwire. */
export const MAX_BYTES = 10 * 1024;

/** What the rendered HTML must never contain (brief §3.8 and §5). */
export const FORBIDDEN = [
  { name: '<style>', re: /<style/i },
  { name: 'class=', re: /\bclass\s*=/i },
  { name: 'max-width', re: /max-width/i },
  { name: 'text-transform', re: /text-transform/i },
  { name: 'flex', re: /flex/i },
  { name: 'grid', re: /grid/i },
];

/** The four image states the preview can draw (§4a). */
export const IMAGE_STATES = [
  { key: 'on', label: 'Images on' },
  { key: 'outlook', label: 'Outlook blocked' },
  { key: 'gmail', label: 'Gmail blocked' },
  { key: 'apple', label: 'Apple Mail blocked' },
];

/** Every value collapses to single spaces — a pasted newline is not a design decision. */
const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();

export const esc = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/**
 * A URL safe for href/src: plainly http(s) or mailto, else empty. The config is
 * Owner-written, but a javascript: URL would sit in everyone's outgoing mail.
 */
export function safeUrl(v) {
  const s = clean(v);
  return /^(https?:\/\/|mailto:)/i.test(s) ? s : '';
}

/** A positive integer, or the fallback — width="NaN" renders the image huge. */
const dim = (v, fallback) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * E.164 from a number as typed (brief §2): strip spaces and brackets, drop "(0)".
 * "+44 (0) 1530 561230" → "+441530561230", "+44 7967 703590" → "+447967703590".
 * Anything that does not come out as +<country><number> returns '' — the caller
 * then omits the tel: href rather than linking to a guess.
 */
export function toE164(raw) {
  const s = clean(raw).replace(/\(0\)/g, '').replace(/[\s()\-.]/g, '');
  return /^\+[1-9]\d{6,14}$/.test(s) ? s : '';
}

/** The brief's regex (§3.5): the space inside a trailing UK postcode becomes &nbsp;. */
export const POSTCODE = /([A-Z]{1,2}\d[A-Z\d]?) (\d[A-Z]{2})$/;
export function nbspPostcode(escapedText) {
  return String(escapedText).replace(POSTCODE, '$1&nbsp;$2');
}

/**
 * Resolve {{#if x}}…{{/if}} innermost-first, on the TEMPLATE (before any data
 * goes in), so a value can never open or close a block.
 */
function resolveIfs(tpl, flags) {
  const re = /\{\{#if (\w+)\}\}((?:(?!\{\{#if )[\s\S])*?)\{\{\/if\}\}/;
  let out = tpl;
  for (let m = re.exec(out); m; m = re.exec(out)) {
    out = out.slice(0, m.index) + (flags[m[1]] ? m[2] : '') + out.slice(m.index + m[0].length);
  }
  return out;
}

/**
 * Everything the template needs, worked out once: the escaped values, the
 * block flags, and any warnings (a number with no derivable tel: link).
 *
 * @param org  organisations/{org}.signature
 * @param user organisations/{org}/users/{uid}
 */
export function model(org = {}, user = {}) {
  const warnings = [];
  const mobile = clean(user.mobile);
  const ownOffice = clean(user.officeTel);
  const officeTel = ownOffice || clean(org.officeTel);
  const mobileE164 = mobile ? toE164(mobile) : '';
  // The org's stored E.164 is used only for the org's own number; a per-user
  // override (India, Birmingham) is derived from what that person typed.
  const officeTelE164 = !officeTel ? ''
    : ownOffice ? toE164(ownOffice)
      : (toE164(org.officeTelE164) || toE164(officeTel));
  if (mobile && !mobileE164) warnings.push(`mobile "${mobile}" has no E.164 form, so it renders without a tel: link`);
  if (officeTel && !officeTelE164) warnings.push(`office number "${officeTel}" has no E.164 form, so it renders without a tel: link`);

  const both = !!(mobile && officeTel);
  const websiteUrl = safeUrl(org.websiteUrl);
  const websiteLabel = clean(org.websiteLabel) || websiteUrl.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');

  const values = {
    logoUrl: esc(safeUrl(org.logoUrl)),
    logoWidth: String(dim(org.logoWidth, 104)),
    logoHeight: String(dim(org.logoHeight, 47)),
    logoAlt: esc(clean(org.logoAlt)),
    descriptorLine1: esc(clean(org.descriptorLine1)),
    descriptorLine2: esc(clean(org.descriptorLine2)),
    // Rule 2: uppercased HERE. Outlook desktop ignores text-transform.
    DISPLAY_NAME: esc(clean(user.displayName).toLocaleUpperCase('en-GB')),
    // Post-nominals keep their own case — "BSc (Hons)" is not "BSC (HONS)".
    postNominals: esc(clean(user.postNominals)),
    // Pre-nominals ("Eur Ing", "Dr") sit before the name in the same grey as the
    // post-nominals, in their own case (Karan 02/11/2026, Steve's signature).
    preNominals: esc(clean(user.preNominals)),
    JOB_TITLE: esc(clean(user.jobTitle).toLocaleUpperCase('en-GB')),
    mobile: esc(mobile),
    mobileE164: esc(mobileE164),
    officeTel: esc(officeTel),
    officeTelE164: esc(officeTelE164),
    // Rule 4: under a mobile the office line is secondary — 2px, ink-2. Alone it
    // takes the mobile row's slot — 12px, ink.
    officeTelPadding: mobile ? '2' : '12',
    officeTelColor: mobile ? TOKENS.ink2 : TOKENS.ink,
    websiteUrl: esc(websiteUrl),
    websiteLabel: esc(websiteLabel),
    registeredName: esc(clean(org.registeredName)),
    placeOfRegistration: esc(clean(org.placeOfRegistration)),
    companyNumber: esc(clean(org.companyNumber)),
    registeredOfficeNbsp: nbspPostcode(esc(clean(org.registeredOffice))),
    legalUrl: esc(safeUrl(org.legalUrl)),
    legalLabel: esc(clean(org.legalLabel)),
  };
  const flags = {
    descriptorLine1: !!values.descriptorLine1,
    descriptorLine2: !!values.descriptorLine2,
    postNominals: !!values.postNominals,
    preNominals: !!values.preNominals,
    mobile: !!mobile,
    officeTel: !!officeTel,
    showLabels: both,                     // Rule 4: one number → no M/T label
  };
  return { values, flags, warnings, mobileE164, officeTelE164 };
}

const COMMENT = /^\s*<!--[\s\S]*?-->\s*/;

function fill(tpl, values) {
  return tpl.replace(/\{\{(\w+)\}\}/g, (m, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : m
  ));
}

function prepare(m) {
  let tpl = TEMPLATE_SOURCE.replace(COMMENT, '');
  // A number with no E.164 form keeps its text and loses only the link.
  if (!m.values.mobileE164) tpl = tpl.replace('<a href="tel:{{mobileE164}}" style=', '<a style=');
  if (!m.values.officeTelE164) tpl = tpl.replace('<a href="tel:{{officeTelE164}}" style=', '<a style=');
  return tpl;
}

const byteLength = (s) => new TextEncoder().encode(s).length;

/**
 * The signature, both ways.
 * @returns {{ html: string, pretty: string, warnings: string[], bytes: number }}
 *   html   — one line, what Gmail gets and what is hashed
 *   pretty — the indented source with values filled in, for the preview UI
 */
export function renderSignatureParts(org = {}, user = {}) {
  const m = model(org, user);
  const tpl = prepare(m);
  const oneLine = tpl.replace(/\s*\n\s*/g, '');
  const html = fill(resolveIfs(oneLine, m.flags), m.values);
  // Pretty: a removed block leaves its indentation behind — drop lines that
  // ended up empty so the source reads as cleanly as the output.
  const pretty = fill(resolveIfs(tpl, m.flags), m.values).replace(/\n[ \t]*(?=\n)/g, '').trim();
  const bytes = byteLength(html);
  if (bytes > MAX_BYTES) throw new Error(`signature is ${bytes} bytes, over the ${MAX_BYTES} limit`);
  return { html, pretty, warnings: m.warnings, bytes };
}

/** The one-line HTML Gmail gets. */
export function renderSignature(org = {}, user = {}) {
  return renderSignatureParts(org, user).html;
}

/** Which forbidden tokens appear in a piece of HTML (empty = clean). */
export function forbiddenIn(html) {
  return FORBIDDEN.filter((f) => f.re.test(String(html))).map((f) => f.name);
}

// ── Org settings validation (§4a: company number 8 digits, legalUrl https) ───
export const ORG_FIELDS = [
  'logoUrl', 'logoWidth', 'logoHeight', 'logoAlt', 'descriptorLine1', 'descriptorLine2',
  'registeredName', 'placeOfRegistration', 'companyNumber', 'registeredOffice', 'legalUrl', 'legalLabel',
  'officeTel', 'officeTelE164', 'websiteUrl', 'websiteLabel', 'gmailDomain',
];

/**
 * @returns {{ ok: boolean, errors: Object<string,string>, value: object }}
 *   value — the cleaned fields, numbers as numbers, officeTelE164 derived
 */
export function validateOrgSignature(input = {}) {
  const errors = {};
  const v = {};
  for (const k of ORG_FIELDS) v[k] = clean(input[k]);
  v.logoWidth = Math.round(Number(v.logoWidth));
  v.logoHeight = Math.round(Number(v.logoHeight));
  if (!/^\d{8}$/.test(v.companyNumber)) errors.companyNumber = 'Eight digits, as on Companies House (e.g. 03438155).';
  if (!/^https:\/\/\S+$/i.test(v.legalUrl)) errors.legalUrl = 'Must start https://';
  if (!/^https:\/\/\S+$/i.test(v.logoUrl)) errors.logoUrl = 'Must start https://';
  if (!/^https?:\/\/\S+$/i.test(v.websiteUrl)) errors.websiteUrl = 'Must start https://';
  if (!Number.isFinite(v.logoWidth) || v.logoWidth < 1 || v.logoWidth > 600) errors.logoWidth = 'A width in pixels.';
  if (!Number.isFinite(v.logoHeight) || v.logoHeight < 1 || v.logoHeight > 600) errors.logoHeight = 'A height in pixels.';
  for (const k of ['logoAlt', 'registeredName', 'placeOfRegistration', 'registeredOffice', 'legalLabel', 'gmailDomain']) {
    if (!v[k]) errors[k] = 'Required.';
  }
  if (v.officeTel) {
    v.officeTelE164 = toE164(v.officeTel);
    if (!v.officeTelE164) errors.officeTel = 'Write it with the country code, e.g. +44 (0) 1530 561230.';
  } else v.officeTelE164 = '';
  return { ok: Object.keys(errors).length === 0, errors, value: v };
}

// ── The preview (§4a) ────────────────────────────────────────────────────────
// Stand-ins drawn as in states.png. They replace the ONE <img> in the template,
// inside the preview frame only.
function standIn(state, { alt, width, height }) {
  const box = `display:block;box-sizing:border-box;width:${width}px;height:${height}px`;
  if (state === 'outlook') {
    // Outlook desktop: a bordered placeholder at the declared size, a red-x icon,
    // and the alt text inside the box, unstyled (it ignores styles on <img>).
    return `<span style="${box};border:1px solid #a8a8a8;padding:3px 4px;background:#ffffff;font-family:Segoe UI,Arial,sans-serif;font-size:12px;line-height:14px;color:#000000;overflow:hidden;white-space:nowrap">`
      + '<span style="display:inline-block;box-sizing:border-box;width:13px;height:13px;margin:0 4px 0 0;border:1px solid #c42b1c;color:#c42b1c;font-family:Arial,sans-serif;font-size:9px;line-height:11px;font-weight:bold;text-align:center;vertical-align:top">x</span>'
      + `${alt}</span>`;
  }
  if (state === 'gmail') {
    // Gmail web/app: the alt as plain, unstyled text in the image's place.
    return `<span style="${box};font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:${height}px;color:#14161b">${alt}</span>`;
  }
  if (state === 'apple') {
    // Apple Mail with remote content off: a grey box with "?", and NO alt text.
    return `<span style="${box};border:1px solid #c8c8c8;background:#f2f2f2;font-family:Helvetica,Arial,sans-serif;font-size:14px;font-weight:bold;line-height:${height - 2}px;text-align:center;color:#9a9a9a">?</span>`;
  }
  return null;
}

/** Outlook-style dark inversion (§4a) — approximate on purpose. */
export const DARK_MAP = { '#14161b': '#e8e8e8', '#5b616d': '#b0b4bb' };

/**
 * The whole document for the preview frame (<iframe sandbox srcdoc>).
 * @param html  rendered signature (one-line or pretty — both are the same HTML)
 * @param opts  { state: 'on'|'outlook'|'gmail'|'apple', dark: boolean }
 */
export function previewDocument(html, { state = 'on', dark = false } = {}) {
  let body = String(html);
  if (state !== 'on') {
    body = body.replace(/<img\b[^>]*>/i, (tag) => {
      const attr = (n) => { const m = new RegExp(`\\b${n}="([^"]*)"`, 'i').exec(tag); return m ? m[1] : ''; };
      return standIn(state, { alt: attr('alt'), width: dim(attr('width'), 104), height: dim(attr('height'), 47) }) || tag;
    });
  }
  if (dark) {
    for (const [from, to] of Object.entries(DARK_MAP)) body = body.split(from).join(to);
  }
  const ground = dark ? 'color-scheme:dark;background:#1f1f1f' : 'color-scheme:light;background:#ffffff';
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="color-scheme" content="${dark ? 'dark' : 'light'}"></head>`
    + `<body style="margin:0;padding:24px 28px;${ground}">${body}</body></html>`;
}
